import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [showGreeting, setShowGreeting] = useState(false);
    const [greetingType, setGreetingType] = useState(null);
    const [isImpersonating, setIsImpersonating] = useState(
        !!localStorage.getItem('impersonation_origin_token')
    );

    // Create a stable API instance
    const api = React.useMemo(() => {
        const instance = axios.create({
            baseURL: `${API_URL}/api`,
            timeout: 15000
        });
        return instance;
    }, []);

    // Update axios headers when token changes
    useEffect(() => {
        if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete api.defaults.headers.common['Authorization'];
        }
    }, [token, api]);

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
            
            // Check for daily login point (once per day per browser session)
            const today = new Date().toISOString().split('T')[0];
            const lastDailyCheck = localStorage.getItem('lastDailyLoginCheck');
            
            if (lastDailyCheck !== today) {
                try {
                    await api.post('/challenges/check-daily-login');
                    localStorage.setItem('lastDailyLoginCheck', today);
                } catch (e) {
                    // Silently fail - not critical
                }
            }
        } catch (error) {
            console.error('Failed to fetch user:', error);
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token, api]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const getGreetingType = (specialEvents) => {
        const hour = new Date().getHours();
        
        if (specialEvents?.is_birthday) return 'birthday';
        if (specialEvents?.returning_from_vacation) return 'vacation_return';
        if (specialEvents?.vacation_tomorrow) return 'vacation_tomorrow';
        if (specialEvents?.is_year_end) return 'year_review';
        if (specialEvents?.is_monday) return 'monday';
        if (specialEvents?.is_month_start) return 'month_start';
        
        if (hour >= 5 && hour < 12) return 'morning';
        if (hour >= 12 && hour < 18) return 'day';
        return 'evening';
    };

    const login = async (username, password) => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/login`, {
                username,
                password
            });
            
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('token', access_token);
            setToken(access_token);
            setUser(userData);

            // Fetch dashboard to get special events
            try {
                const dashResponse = await axios.get(`${API_URL}/api/dashboard`, {
                    headers: { Authorization: `Bearer ${access_token}` }
                });
                const greeting = getGreetingType(dashResponse.data.special_events);
                setGreetingType(greeting);
                setShowGreeting(true);
                
                // Auto-hide greeting after 4 seconds
                setTimeout(() => setShowGreeting(false), 4000);
            } catch {
                // Show default greeting
                setGreetingType(getGreetingType(null));
                setShowGreeting(true);
                setTimeout(() => setShowGreeting(false), 4000);
            }

            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Anmeldung fehlgeschlagen'
            };
        }
    };

    const loginWithToken = async (accessToken, userData) => {
        localStorage.setItem('token', accessToken);
        setToken(accessToken);
        setUser(userData);
        setGreetingType(getGreetingType(null));
        setShowGreeting(true);
        setTimeout(() => setShowGreeting(false), 4000);
    };

    /**
     * Start impersonating another user.
     * Stashes the current (admin) token so we can switch back later.
     */
    const startImpersonation = async (accessToken, targetUserData) => {
        // Save current admin session for "switch back" (always overwrite if a new session starts)
        const currentToken = localStorage.getItem('token');
        if (currentToken && !localStorage.getItem('impersonation_origin_token')) {
            localStorage.setItem('impersonation_origin_token', currentToken);
            if (user) {
                try { localStorage.setItem('impersonation_origin_user', JSON.stringify(user)); } catch (e) { /* ignore */ }
            }
        }
        localStorage.setItem('token', accessToken);
        setToken(accessToken);
        setUser(targetUserData);
        setIsImpersonating(true);
    };

    /**
     * Restore the original admin session and clear impersonation state.
     */
    const stopImpersonation = () => {
        const originToken = localStorage.getItem('impersonation_origin_token');
        if (!originToken) {
            setIsImpersonating(false);
            return false;
        }
        const originUserRaw = localStorage.getItem('impersonation_origin_user');
        localStorage.removeItem('impersonation_origin_token');
        localStorage.removeItem('impersonation_origin_user');
        localStorage.setItem('token', originToken);
        setToken(originToken);
        if (originUserRaw) {
            try { setUser(JSON.parse(originUserRaw)); } catch { /* fetchUser will refresh */ }
        }
        setIsImpersonating(false);
        return true;
    };


    const loginWithGoogle = async (credential) => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/google`, {
                credential
            });
            const { access_token, user: userData } = response.data;
            await loginWithToken(access_token, userData);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Google-Anmeldung fehlgeschlagen'
            };
        }
    };

    const logout = (showAnimation = true, vacationTomorrow = false) => {
        // Always clear impersonation state on logout
        localStorage.removeItem('impersonation_origin_token');
        localStorage.removeItem('impersonation_origin_user');
        setIsImpersonating(false);
        if (showAnimation) {
            setGreetingType(vacationTomorrow ? 'logout_vacation' : 'logout');
            setShowGreeting(true);
            
            setTimeout(() => {
                localStorage.removeItem('token');
                setToken(null);
                setUser(null);
                setShowGreeting(false);
            }, 3000);
        } else {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
        }
    };

    const dismissGreeting = () => {
        setShowGreeting(false);
    };

    // Update user data locally without refetch
    const updateUser = (updates) => {
        setUser(prev => ({ ...prev, ...updates }));
    };

    const value = {
        user,
        token,
        loading,
        login,
        loginWithGoogle,
        loginWithToken,
        startImpersonation,
        stopImpersonation,
        isImpersonating,
        logout,
        api,
        showGreeting,
        greetingType,
        dismissGreeting,
        refreshUser: fetchUser,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
